"use client";

import { useEffect, useState, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
  features: string[];
  color: string;
}

const ServiceGrid = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const services: Service[] = [
    {
      id: "service_brokerage",
      title: "Trading and Brokerage solutions",
      description: "We facilitate trades across agricultural commodities and industrial products.",
      icon: "ChartBarIcon",
      features: [
        "Secure escrow services",
        "Ability to support LCs issued from the most difficult to most covered jurisdictions",
      ],
      color: "bg-primary/10 text-primary border-primary/20",
    },
    {
      id: "service_logistics",
      title: "Logistics & Shipping",
      description: "End-to-end supply chain management from warehouse to destination.",
      icon: "TruckIcon",
      features: [
        "Global shipping network",
      ],
      color: "bg-accent/10 text-accent border-accent/20",
    },
    {
      id: "service_quality",
      title: "Quality Assurance",
      description: "ISO-certified testing and inspection services. Third-party lab analysis and quality certificates for all shipments.",
      icon: "ShieldCheckIcon",
      features: [
        "Lab testing & analysis",
        "Quality certificates",
        "Compliance verification",
        "Sampling services",
      ],
      color: "bg-success/10 text-success border-success/20",
    },
  ];

  return (
    <section data-component-id="src/app/services/components/ServiceGrid.tsx:74:4" data-component-path="src/app/services/components/ServiceGrid.tsx" data-component-line="74" data-component-end-line="117" data-component-file="ServiceGrid.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-24%20bg-gradient-to-b%20from-background%20to-muted%2F30%20pt-0%22%7D"
      ref={sectionRef}
      className="py-24 bg-gradient-to-b from-background to-muted/30 pt-0"
    >
      <div data-component-id="src/app/services/components/ServiceGrid.tsx:78:6" data-component-path="src/app/services/components/ServiceGrid.tsx" data-component-line="78" data-component-end-line="116" data-component-file="ServiceGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22container-custom%22%7D" className="container-custom">
        {/* Service Cards */}
        <div data-component-id="src/app/services/components/ServiceGrid.tsx:80:8" data-component-path="src/app/services/components/ServiceGrid.tsx" data-component-line="80" data-component-end-line="115" data-component-file="ServiceGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-8%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div data-component-id="src/app/services/components/ServiceGrid.tsx:82:12" data-component-path="src/app/services/components/ServiceGrid.tsx" data-component-line="82" data-component-end-line="113" data-component-file="ServiceGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
              key={service.id}
              className={`glass-panel rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 hover:scale-105 group ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Icon */}
              <div data-component-id="src/app/services/components/ServiceGrid.tsx:90:14" data-component-path="src/app/services/components/ServiceGrid.tsx" data-component-line="90" data-component-end-line="92" data-component-file="ServiceGrid.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform ${service.color} border-2`}>
                <Icon data-component-id="src/app/services/components/ServiceGrid.tsx:91:16" data-component-path="src/app/services/components/ServiceGrid.tsx" data-component-line="91" data-component-end-line="91" data-component-file="ServiceGrid.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={service.icon as any} size={32} variant="solid" />
              </div>

              {/* Title */}
              <h3 data-component-id="src/app/services/components/ServiceGrid.tsx:95:14" data-component-path="src/app/services/components/ServiceGrid.tsx" data-component-line="95" data-component-end-line="97" data-component-file="ServiceGrid.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-2xl%20font-heading%20font-bold%20mb-4%20text-foreground%22%7D" className="text-2xl font-heading font-bold mb-4 text-foreground">
                {service.title}
              </h3>

              {/* Description */}
              <p data-component-id="src/app/services/components/ServiceGrid.tsx:100:14" data-component-path="src/app/services/components/ServiceGrid.tsx" data-component-line="100" data-component-end-line="102" data-component-file="ServiceGrid.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20leading-relaxed%20mb-6%22%7D" className="text-muted-foreground leading-relaxed mb-6">
                {service.description}
              </p>

              {/* Features */}
              <ul data-component-id="src/app/services/components/ServiceGrid.tsx:105:14" data-component-path="src/app/services/components/ServiceGrid.tsx" data-component-line="105" data-component-end-line="112" data-component-file="ServiceGrid.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22space-y-3%22%7D" className="space-y-3">
                {service.features.map((feature, idx) => (
                  <li data-component-id="src/app/services/components/ServiceGrid.tsx:107:18" data-component-path="src/app/services/components/ServiceGrid.tsx" data-component-line="107" data-component-end-line="110" data-component-file="ServiceGrid.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%2C%22className%22%3A%22flex%20items-center%20gap-3%20text-sm%22%7D" key={idx} className="flex items-center gap-3 text-sm">
                    <Icon data-component-id="src/app/services/components/ServiceGrid.tsx:108:20" data-component-path="src/app/services/components/ServiceGrid.tsx" data-component-line="108" data-component-end-line="108" data-component-file="ServiceGrid.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22text-success%20flex-shrink-0%22%7D" name="CheckCircleIcon" size={18} className="text-success flex-shrink-0" variant="solid" />
                    <span data-component-id="src/app/services/components/ServiceGrid.tsx:109:20" data-component-path="src/app/services/components/ServiceGrid.tsx" data-component-line="109" data-component-end-line="109" data-component-file="ServiceGrid.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-muted-foreground%22%7D" className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceGrid;